import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, Dialog, Field, Input, MultiSelect, Select } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  clientLabel,
  LazyProductPricingClient,
  NexpimClientClient,
  type DiscountProfit,
  type LazyProductPricingDTO,
} from "../../api/pricing";
import { basePricingColumns } from "./pricingShared";

/**
 * Pricing → Product discounts: pick a client and a discount %, and the server
 * simulates each product at that discounted price. Only products that survive
 * the floor are listed — a real contracted price (`first ≠ 0`) and a net
 * margin (`third`) still at or above the global MIN_DISCOUNT_NET_MARGIN rule.
 */
export function ProductDiscountPage() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const { start: startExport, busy } = useExport();

  const pricing = useMemo(() => new LazyProductPricingClient(http), [http]);
  const clients = useMemo(() => new NexpimClientClient(http), [http]);

  const [clientId, setClientId] = useState<string>("");
  const [discount, setDiscount] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportClients, setExportClients] = useState<string[]>([]);
  const [exportDiscount, setExportDiscount] = useState<number>(10);

  // Discounting covers bundles too, unlike the plain pricing screen.
  const productsQuery = useQuery({
    queryKey: ["pricing", "discount", "products"],
    queryFn: () =>
      pricing.byTypeAndStatus(
        ["SINGLE_ITEM", "BUNDLE"],
        ["AVAILABLE", "PENDING_DISCONTINUED", "PENDING_LAUNCH"],
      ),
    staleTime: 5 * 60_000,
  });

  const globalRuleQuery = useQuery({
    queryKey: ["config", "global-pricing-rule"],
    queryFn: () =>
      http.get<Array<{ id: number; type: string; value: number }>>("/global/pricing/rule"),
    staleTime: 30 * 60_000,
  });
  const minNetMargin = (globalRuleQuery.data ?? []).find(
    (rule) => rule.type === "MIN_DISCOUNT_NET_MARGIN",
  )?.value;

  // Clients with an order in the last two years, as the Vaadin picker did.
  const clientListQuery = useQuery({
    queryKey: ["client", "last-order-2y"],
    queryFn: () => clients.byLastOrder(2),
    staleTime: 30 * 60_000,
  });

  const profitQuery = useQuery({
    queryKey: ["pricing", "discount-profit", clientId, discount],
    queryFn: () =>
      pricing.discountProfits(
        Number(clientId),
        (productsQuery.data ?? []).map((row) => row.id),
        discount,
      ),
    enabled: clientId !== "" && discount >= 0 && Boolean(productsQuery.data),
    staleTime: 60_000,
  });

  const selectedClient = (clientListQuery.data ?? []).find((row) => String(row.cli_id) === clientId);
  const currency = selectedClient?.cli_currency ?? "";

  // The Vaadin floor: a real price, and net margin still above the global rule.
  const rows = useMemo(() => {
    const profits = profitQuery.data;
    if (!profits || minNetMargin === undefined) return [];
    return (productsQuery.data ?? []).filter((row) => {
      const tuple = profits[String(row.id)];
      return tuple !== undefined && tuple.first !== 0 && tuple.third >= minNetMargin / 100;
    });
  }, [productsQuery.data, profitQuery.data, minNetMargin]);

  const cols = useMemo(() => {
    const c = defineColumns<LazyProductPricingDTO>();
    const profits = profitQuery.data ?? {};
    const tuple = (row: LazyProductPricingDTO): DiscountProfit | undefined =>
      profits[String(row.id)];

    return [
      ...basePricingColumns(c),
      c.number("price", (row) => {
        const value = tuple(row)?.first;
        return value === undefined || value === 0 ? null : Math.round(value * 100) / 100;
      }, { headerKey: "pricing.current-price", priority: 3 }),
      c.custom(
        "net_margin",
        (row) => Math.round((tuple(row)?.third ?? 0) * 1000) / 10,
        (row) => {
          const values = tuple(row);
          if (!values || values.first === 0) {
            return <span className="text-ink-faint">{t("pricing.no-data", "no data")}</span>;
          }
          return (
            <span className="tabular">
              {(values.third * 100).toFixed(1)}%
              <span className="ml-1 text-[11px] text-ink-faint">
                {Math.round(values.second)} {currency}
              </span>
            </span>
          );
        },
        { headerKey: "pricing.client-net", priority: 3 },
      ),
    ];
  }, [profitQuery.data, currency, t]);

  const clientOptions = (clientListQuery.data ?? [])
    .map((row) => ({ value: String(row.cli_id), label: clientLabel(row) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const runExport = () => {
    setExportOpen(false);
    void startExport(
      "pricing-discount-export",
      () => pricing.exportPricingDiscount(exportClients.map(Number), exportDiscount),
      t("job.download.failed", "Download failed"),
    );
  };

  return (
    <>
      <PageHeader
        title={t("navigation.item.pricing-product-discount", "Discounts")}
        subtitle={
          minNetMargin !== undefined
            ? t("pricing.discount-floor", "≥ {{margin}}% net margin at this discount.", {
                margin: minNetMargin,
              })
            : undefined
        }
      />

      {globalRuleQuery.isSuccess && minNetMargin === undefined ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t(
              "notification.missing-pricing-rule.title",
              "The MIN_DISCOUNT_NET_MARGIN global pricing rule is missing.",
            )}
          </p>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={cols}
          getRowId={(row) => String(row.id)}
          loading={productsQuery.isLoading || (clientId !== "" && profitQuery.isLoading)}
          // The simulation inputs sit right after the search, where every grid
          // keeps the control that decides what it shows; they are not actions.
          toolbarLead={
            <div className="flex items-center gap-2">
              <label htmlFor="discount-client" className="text-[12px] text-ink-muted">
                {t("column.client", "Client")}
              </label>
              <Select
                id="discount-client"
                value={clientId}
                placeholder={t("pricing.select-client", "Select client")}
                options={clientOptions}
                onChange={(event) => setClientId(event.target.value)}
                className="h-8 w-56"
              />
              <label htmlFor="discount-percent" className="ml-1 text-[12px] text-ink-muted">
                {t("form.field.discount", "Discount %")}
              </label>
              <Input
                id="discount-percent"
                type="number"
                min={0}
                max={100}
                value={String(discount)}
                onChange={(event) => setDiscount(Number(event.target.value))}
                className="h-8 w-20"
              />
            </div>
          }
          actions={[
            {
              label: t("pricing.export-discount", "Export discounts"),
              icon: <Download className="size-4" />,
              disabled: busy !== null,
              onClick: () => setExportOpen(true),
            },
          ]}
          emptyMessage={
            clientId === ""
              ? t("pricing.pick-client", "Pick a client and discount to run the simulation.")
              : t("table.empty", "Nothing to show")
          }
        />
      )}

      <Dialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={t("dialog.export-pricing-discount.title", "Export discounted pricing")}
        actions={
          <>
            <Button variant="ghost" onClick={() => setExportOpen(false)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button onClick={runExport} disabled={exportClients.length === 0}>
              {t("dialog.confirm.export", "Export")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label={t("pricing.export-clients", "Clients")} htmlFor="export-clients">
            <MultiSelect
              id="export-clients"
              options={clientOptions}
              value={exportClients}
              onChange={setExportClients}
              placeholder={t("pricing.select-clients", "Select clients")}
            />
          </Field>
          <Field label={t("form.field.discount", "Discount %")} htmlFor="export-discount">
            <Input
              id="export-discount"
              type="number"
              min={0}
              max={100}
              value={String(exportDiscount)}
              onChange={(event) => setExportDiscount(Number(event.target.value))}
              className="h-8 w-24"
            />
          </Field>
        </div>
      </Dialog>
    </>
  );
}
